import React from 'react';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  DMFRecord,
  BreakLinkResponse,
} from '../../services/api/domains/products/types';
import { GRAY_500, GRAY_900 } from 'theme/grayScale';
import { DMF_PANEL_COLORS } from 'theme/sourcePanelColors';
import {
  formatDmfType as formatType,
  formatDmfStatus as formatStatus,
} from '../../utils/dmfTypeLabels';
import { formatDateTime } from '../../utils/dateConversion';
import LgmButton from '../LgmButton';
import CollapsibleSection from './CollapsibleSection';
import SourcePanelEmptyState from './SourcePanelEmptyState';
import BreakLinkConfirmDialog from './BreakLinkConfirmDialog';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function recordLabel(record: DMFRecord): string {
  return record.subject || record.holder || `DMF ${record.dmf_number}`;
}

// ---------------------------------------------------------------------------
// Sub-component — a single read-only label/value row
// ---------------------------------------------------------------------------

const FieldRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div
    style={{
      display: 'flex',
      gap: '8px',
      padding: '4px 0',
      fontSize: '14px',
      lineHeight: '1.4',
    }}>
    <span
      style={{
        minWidth: '120px',
        fontWeight: 600,
        color: GRAY_500,
        flexShrink: 0,
      }}>
      {label}
    </span>
    <span style={{ color: GRAY_900, wordBreak: 'break-word' }}>{value}</span>
  </div>
);

// ---------------------------------------------------------------------------
// DMFPanel — issue #522, Sub-task 7.3, extended by issue #622 Task 8
// (three-state matrix, collapse behaviour, break-link control)
// ---------------------------------------------------------------------------

interface DMFPanelProps {
  productId: number;
  dmfRecords: DMFRecord[];
  /** Admin AND edit mode — both gate the break-link icon and the add-link control. */
  isAdmin: boolean;
  isEditMode: boolean;
  /**
   * Opens the Task 9 Link-to-Source modal, scoped to USDMF. That modal
   * doesn't exist yet (issue #622 Task 9 is separate work) — this is a
   * placeholder trigger point only.
   */
  onAddLink: () => void;
}

/**
 * USDMF (Drug Master File) section for the Product modal. Always renders
 * (issue #622, Sub-task 8.1) — a product can legitimately link to several
 * DMF filings, so this renders one block per record. FDA is the
 * authoritative source — no DMF data here is directly editable; the only
 * write action is breaking a link (Sub-task 8.3).
 */
const DMFPanel: React.FC<DMFPanelProps> = ({
  productId,
  dmfRecords,
  isAdmin,
  isEditMode,
  onAddLink,
}) => {
  const [brokenIds, setBrokenIds] = React.useState<Set<number>>(new Set());
  const [breakTarget, setBreakTarget] = React.useState<{
    recordId: number;
    label: string;
  } | null>(null);

  // Also reset on a genuinely fresh `dmfRecords` array (a real refetch, e.g.
  // after Task 9's link-create refresh) -- not just productId. Otherwise
  // breaking then re-linking the SAME underlying record within one modal
  // session leaves it permanently filtered out here: brokenIds is additive
  // and this was its only reset path, so a resurrected record with the same
  // dmf_product_id stayed hidden even though fresh server data proved it
  // linked again. Found live via the Playwright MCP (issue #622).
  React.useEffect(() => {
    setBrokenIds(new Set());
    setBreakTarget(null);
  }, [productId, dmfRecords]);

  const canWrite = isAdmin && isEditMode;
  const records = dmfRecords.filter(
    r => r.dmf_product_id === undefined || !brokenIds.has(r.dmf_product_id)
  );

  if (records.length === 0) {
    return (
      <SourcePanelEmptyState
        badgeText="FDA"
        sourceLabel="USDMF"
        palette={DMF_PANEL_COLORS}
        canAdd={canWrite}
        onAdd={onAddLink}
      />
    );
  }

  const handleBroken = (result: BreakLinkResponse) => {
    setBrokenIds(prev => new Set(prev).add(result.record_id));
    setBreakTarget(null);
  };

  return (
    <div
      style={{
        margin: '20px 0 8px 0',
        border: `1px solid ${DMF_PANEL_COLORS.border}`,
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: DMF_PANEL_COLORS.background,
      }}>
      {/* Panel header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: DMF_PANEL_COLORS.headerBackground,
          borderBottom: `1px solid ${DMF_PANEL_COLORS.border}`,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: DMF_PANEL_COLORS.badgeText,
              backgroundColor: DMF_PANEL_COLORS.badgeBackground,
              padding: '2px 6px',
              borderRadius: '4px',
            }}>
            FDA - USDMF
          </span>
        </div>
        {/* A source can legitimately have multiple links -- the zero-link
            empty state's add button (SourcePanelEmptyState) only covers
            going from 0 to 1. Without this, an admin with an existing link
            has no way to add another. Found live via the Playwright MCP
            (issue #622). */}
        {canWrite && (
          <LgmButton
            variant="outlined"
            onClick={onAddLink}
            style={{ fontSize: '12px', padding: '4px 10px' }}>
            + Link to USDMF
          </LgmButton>
        )}
      </div>

      <div style={{ padding: '8px 12px' }}>
        <CollapsibleSection
          title={`${records.length} link${records.length === 1 ? '' : 's'}`}
          defaultCollapsed={records.length > 1}>
          {records.map((record, idx) => (
            <div
              key={`${record.dmf_number}-${idx}`}
              style={{
                borderTop:
                  idx > 0 ? `1px solid ${DMF_PANEL_COLORS.border}` : undefined,
                paddingTop: idx > 0 ? '8px' : undefined,
                marginTop: idx > 0 ? '8px' : undefined,
              }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <FieldRow
                    label="DMF Number"
                    value={String(record.dmf_number)}
                  />
                  <FieldRow label="Type" value={formatType(record.dmf_type)} />
                  <FieldRow
                    label="Status"
                    value={formatStatus(record.status)}
                  />
                  <FieldRow label="Subject" value={record.subject ?? '—'} />
                  <FieldRow label="Holder" value={record.holder} />
                  <FieldRow
                    label="Submit Date"
                    value={formatDateTime(record.submit_date) || '—'}
                  />
                </div>
                {canWrite && record.dmf_product_id !== undefined && (
                  <LgmButton
                    variant="flat"
                    color="danger"
                    onClick={() =>
                      setBreakTarget({
                        recordId: record.dmf_product_id as number,
                        label: recordLabel(record),
                      })
                    }
                    aria-label={`Break link to USDMF record ${recordLabel(record)}`}
                    style={{ padding: '4px', minWidth: 'auto', flexShrink: 0 }}>
                    <LinkOffIcon style={{ fontSize: '18px' }} />
                  </LgmButton>
                )}
              </div>
            </div>
          ))}
        </CollapsibleSection>
      </div>

      {breakTarget && (
        <BreakLinkConfirmDialog
          productId={productId}
          source="dmf"
          sourceLabel="USDMF"
          recordId={breakTarget.recordId}
          recordLabel={breakTarget.label}
          onClose={() => setBreakTarget(null)}
          onBroken={handleBroken}
        />
      )}
    </div>
  );
};

export default DMFPanel;
